import { Grid, Button, useTheme, ThemeProvider, StyledEngineProvider } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectOrderDetailsUIView } from 'app/components/Order/Store/Order.selectors';
import { withReadOnly } from 'oidc/withReadOnly';
import { selectCreateOrderOcbr } from 'store/redux-store/create-order-ocbr/selector';
import { GenericDialog } from '@AMIEWEB/Alerts/GenericDialog';

const useStyles = makeStyles<{ onEditScreen: boolean }>()((theme, { onEditScreen }) => ({
  container: {
    display: 'flex',
    backgroundColor: 'rgba(255, 255, 255, 0.6)',
    height: 60,
    width: 'calc(100% - 92px)',
    position: 'fixed',
    left: 61,
    right: 0,
    bottom: '0px',
    opacity: 0.9,
  },
  elevateContainer: {
    zIndex: 1100,
    backdropFilter: 'blur(2px)',
  },
  lowerContainer: {
    zIndex: 0,
  },
  orderButton: {
    marginRight: 10,
    marginTop: 10,
    marginBottom: 6,
  },
  wrapper: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
}));

interface Props {
  openClearChangesConfirmation: boolean;
  blockNavigation: boolean;
  modalOpen: boolean;
  disableOptions: boolean;
  handleSaveOrder: () => Promise<void>;
  handleClearChanges: () => void;
  handleClearChangesConfirmClose: (modalState: boolean, clearValues: boolean) => void;
}

export const OrderDetailsButtonWrapper = (props: Props) => {
  const {
    openClearChangesConfirmation,
    blockNavigation,
    modalOpen,
    disableOptions,
    handleClearChanges,
    handleSaveOrder,
    handleClearChangesConfirmClose,
  } = props;
  const { t } = useTranslation();
  const theme = useTheme();
  const [disableBtn, setDisableBtn] = useState(true);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState<boolean>(openClearChangesConfirmation);
  const selectOrderUiDetails = useSelector(selectOrderDetailsUIView);
  const { ocbrDetails, ocbrDetailsSource } = useSelector(selectCreateOrderOcbr);

  const hasRatesBeenEdited = React.useMemo(
    () => JSON.stringify(ocbrDetailsSource) !== JSON.stringify(ocbrDetails),
    [ocbrDetailsSource, ocbrDetails],
  );

  //@Team @Rijash - remove unused functions
  const reloadHandler = e => {
    e.preventDefault();
    e.returnValue = '';
    window.removeEventListener('beforeunload', reloadHandler);
    setShowConfirmationDialog(true);
  };

  useEffect(() => {
    setShowConfirmationDialog(openClearChangesConfirmation);
  }, [openClearChangesConfirmation]);

  const closePopupAndSaveDetails = () => {
    handleClearChangesConfirmClose(false, false);
    setShowConfirmationDialog(false);
    handleSaveOrder();
  };

  useEffect(() => {
    setDisableBtn(selectOrderUiDetails?.currentStep === 'editRateCard' ? !hasRatesBeenEdited : disableOptions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasRatesBeenEdited, disableOptions]);

  useEffect(() => {
    if (selectOrderUiDetails) {
      const placementLength = parseFloat(selectOrderUiDetails?.atAGlance?.placementLength);
      if (!isNaN(placementLength)) {
        if (placementLength < 0) {
          setDisableBtn(true);
        } else {
          setDisableBtn(disableOptions);
        }
      }
    }
  }, [selectOrderUiDetails]);

  /*useEffect(() => {
    if (editStarted || hasRatesBeenEdited) {
      window.addEventListener('beforeunload', reloadHandler);
      return () => {
        window.removeEventListener('beforeunload', reloadHandler);
      };
    } else {
      window.removeEventListener('beforeunload', reloadHandler);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editStarted, hasRatesBeenEdited]);*/

  const onEditScreen = React.useMemo(
    () => ['editRateCard'].includes(selectOrderUiDetails?.currentStep),
    [selectOrderUiDetails?.currentStep],
  );
  const { classes } = useStyles({ onEditScreen });

  return (
    <>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <Grid
            container
            direction="row"
            justifyContent="flex-end"
            className={
              openClearChangesConfirmation || blockNavigation || modalOpen
                ? `${classes.container} ${classes.lowerContainer}`
                : `${classes.container} ${classes.elevateContainer}`
            }
          >
            <Grid item className={classes.wrapper} xs={5} lg={4}>
              <Button
                variant="contained"
                color="tertiary"
                size="large"
                className={`${classes.orderButton}`}
                disabled={onEditScreen ? false : disableBtn}
                onClick={handleClearChanges}
              >
                {onEditScreen ? 'Cancel' : t('order.orderDetails.buttonClearChanges')}
              </Button>
              {withReadOnly(Button)({
                variant: 'contained',
                color: 'primary',
                size: "large",
                className: `${classes.orderButton}`,
                disabled: disableBtn,
                onClick: handleSaveOrder,
                children: onEditScreen ? 'Save' : t('order.orderDetails.buttonSaveChanges'),
              })}
            </Grid>
          </Grid>
          <GenericDialog
            open={showConfirmationDialog}
            disableEscapeKeyDown
            maxWidth="sm"
            fullWidth
            dialogTitleProps={{
              text: onEditScreen ? t('order.orderDetails.editConfirmation') : t('order.orderDetails.contentConfirmation'),
            }}
            dialogActions={[
              {
                text: onEditScreen
                ? t('order.orderDetails.buttonConfirmation.Cancel')
                : t('order.orderDetails.buttonConfirmation.No'),
                variant: 'text',
                color: 'tertiary',
                onClick: () => handleClearChangesConfirmClose(false, false),
              },
              {
                text: t('order.orderDetails.buttonConfirmation.No'),
                variant: 'contained',
                hidden: !onEditScreen,
                color: 'tertiary',
                onClick: () => handleClearChangesConfirmClose(false, true),
              },
              {
                text: t('order.orderDetails.buttonConfirmation.Yes'),
                variant: 'contained',
                color: 'primary',
                onClick: () => onEditScreen ? closePopupAndSaveDetails() : handleClearChangesConfirmClose(false, true),
              },
            ]}
          >
         </GenericDialog>
        </ThemeProvider>
      </StyledEngineProvider>
    </>
  );
};
